import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchPublicPricing } from "../redux/reducers/pricingSlice";
import logo from "../assets/logo.png";
import { FiCheck, FiMenu, FiX } from "react-icons/fi";

// Brand palette
// Deep Plum #3d184e | Teal #2e97a9 | Navy #2a496e | Gold #C9A84C | Lavender Gray #a4a0c6 | Off White #f3fafb

const LandingPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pricing } = useSelector((state) => state.pricing);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchPublicPricing());
  }, [dispatch]);

  const monthlyPrice = pricing?.monthlyPrice ?? 149;

  return (
    <div className="min-h-screen bg-white font-sans">

      {/* ── NAVBAR ──────────────────────────────────────────────────────── */}
      <header className="bg-[#3d184e] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img src={logo} alt="Logo" className="h-8 w-auto" />
              <span className="text-white font-bold text-lg">
                Revenue Pro Planner<span className="text-[#C9A84C]">™</span>
              </span>
            </div>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-[#a4a0c6] hover:text-white text-sm transition-colors">Features</a>
              <a href="#pricing" className="text-[#a4a0c6] hover:text-white text-sm transition-colors">Pricing</a>
              <a href="#about" className="text-[#a4a0c6] hover:text-white text-sm transition-colors">About</a>
              <Link to="/login" className="text-[#a4a0c6] hover:text-white text-sm transition-colors">Sign In</Link>
              <button
                onClick={() => navigate("/trial")}
                className="bg-[#C9A84C] text-[#3d184e] font-bold text-sm px-4 py-1.5 rounded-lg hover:brightness-110 transition-all"
              >
                Start Free Trial
              </button>
            </nav>

            {/* Mobile menu button */}
            <button className="md:hidden text-[#a4a0c6] hover:text-white" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <FiX className="text-xl" /> : <FiMenu className="text-xl" />}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {menuOpen && (
          <div className="md:hidden border-t border-[#5a2a70] px-4 py-4 space-y-3">
            <a href="#features" className="block text-[#a4a0c6] hover:text-white text-sm py-1" onClick={() => setMenuOpen(false)}>Features</a>
            <a href="#pricing" className="block text-[#a4a0c6] hover:text-white text-sm py-1" onClick={() => setMenuOpen(false)}>Pricing</a>
            <a href="#about" className="block text-[#a4a0c6] hover:text-white text-sm py-1" onClick={() => setMenuOpen(false)}>About</a>
            <Link to="/login" className="block text-[#a4a0c6] hover:text-white text-sm py-1" onClick={() => setMenuOpen(false)}>Sign In</Link>
            <button
              onClick={() => { setMenuOpen(false); navigate("/trial"); }}
              className="w-full bg-[#C9A84C] text-[#3d184e] font-bold text-sm py-2 rounded-lg"
            >
              Start Free Trial
            </button>
          </div>
        )}
      </header>

      {/* ── HERO ────────────────────────────────────────────────────────── */}
      <section className="bg-[#3d184e] pt-20 pb-24">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-[#2e97a9] text-xs font-bold uppercase tracking-widest mb-6">
            Revenue Intelligence for Beauty &amp; Barber Schools
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
            Finally. A Revenue Planning Tool Built for{" "}
            <span className="text-[#C9A84C]">Beauty Schools.</span>
          </h1>
          <p className="text-[#a4a0c6] text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Project your tuition income. Monitor your clinic floor. Forecast your profit. All in one place.
          </p>
          <p className="text-gray-400 text-sm mb-2 italic">
            Built by a former campus president and national operations director who actually ran the numbers — and built the tool she wished she had.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8 mb-4">
            <button
              onClick={() => navigate("/trial")}
              className="bg-[#C9A84C] text-[#3d184e] font-bold px-8 py-3.5 rounded-xl text-base hover:brightness-110 transition-all w-full sm:w-auto"
            >
              Start My Free Trial
            </button>
          </div>
          <p className="text-[#a4a0c6] text-sm mb-10">No credit card required · Cancel anytime</p>

          {/* Stat row */}
          <div
            className="grid grid-cols-3 gap-4 max-w-lg mx-auto pt-8"
            style={{ borderTop: "0.5px solid rgba(164,160,198,0.25)" }}
          >
            {[
              { num: "5", label: "Planning modules" },
              { num: "2", label: "Revenue streams tracked" },
              { num: "1", label: "Tool built for this industry" },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-[#C9A84C] text-2xl sm:text-3xl font-bold">{s.num}</p>
                <p className="text-[#a4a0c6] text-xs uppercase tracking-wide mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROBLEM ─────────────────────────────────────────────────────── */}
      <section className="bg-[#f3fafb] py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[#2e97a9] text-xs font-bold uppercase tracking-widest mb-3">Sound familiar?</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2a496e] mb-5">
              Most school owners are running their business blind.
            </h2>
            <div className="w-10 h-[3px] bg-[#C9A84C] mx-auto" />
          </div>

          {/* Tiles 1–4: 2-col grid, Plum left border */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {[
              {
                title: "You don't know how many leads you actually need.",
                body: "You set an enrollment goal but have no math connecting that number to your lead flow, contact rate, or appointment show rate.",
              },
              {
                title: "You can't see your revenue coming — until it's already gone.",
                body: "No forward visibility into tuition income by month. No early warning when a start date is too low to cover expenses.",
              },
              {
                title: "Student drops hit you hard and you never saw it coming.",
                body: "A student drops mid-program and the income disappears. There's no system tracking that impact on your monthly P&L.",
              },
              {
                title: "Your clinic floor has revenue potential you're not measuring.",
                body: "You have students on the floor every day but no tool telling you what they need to produce — per student, per day — to hit your goal.",
              },
            ].map((p) => (
              <div
                key={p.title}
                className="bg-white border border-gray-200 rounded-xl p-5"
                style={{ borderLeft: "3px solid #3d184e" }}
              >
                <p className="text-sm font-bold text-[#3d184e] mb-2">{p.title}</p>
                <p className="text-xs text-gray-600 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>

          {/* Tile 5: full-width, Teal left border */}
          <div
            className="bg-white border border-gray-200 rounded-xl p-5"
            style={{ borderLeft: "3px solid #2e97a9" }}
          >
            <p className="text-sm font-bold text-[#2a496e] mb-2">
              NACCAS wants proof of financial stability. You're winging it.
            </p>
            <p className="text-xs text-gray-600 leading-relaxed">
              The accreditation audit requires documented evidence of financial viability. A spreadsheet and good intentions won't cut it.
            </p>
          </div>

          <p className="text-center text-sm text-[#2a496e] italic mt-8">
            Revenue Pro Planner™ was built to fix all of this — in one tool, designed specifically for how beauty schools earn money.
          </p>
        </div>
      </section>

      {/* ── SOLUTION ────────────────────────────────────────────────────── */}
      <section className="bg-white py-20">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <p className="text-[#2e97a9] text-xs font-bold uppercase tracking-widest mb-3">
            Introducing Revenue Pro Planner™
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2a496e] mb-5">
            The only revenue planning system built around how beauty schools actually make money.
          </h2>
          <div className="w-10 h-[3px] bg-[#C9A84C] mx-auto mb-7" />
          <p className="text-gray-600 text-base leading-relaxed mb-5">
            Beauty and barber schools earn revenue in exactly two ways: student enrollments and clinic floor service sales.
            Revenue Pro Planner™ was engineered around that reality — not adapted from a generic accounting template. It gives you
            the forward-looking visibility to plan, project, adjust, and protect your income before problems become crises.
          </p>
          <p className="text-gray-500 text-sm italic">
            Used internally by campus presidents at multi-campus school networks. Now available to every independent school owner.
          </p>
        </div>
      </section>

      {/* ── FIVE MODULES ────────────────────────────────────────────────── */}
      <section id="features" className="bg-[#f3fafb] py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[#2e97a9] text-xs font-bold uppercase tracking-widest mb-3">Five modules</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2a496e] mb-5">
              One complete financial operating system.
            </h2>
            <div className="w-10 h-[3px] bg-[#C9A84C] mx-auto" />
          </div>

          {/* Modules 01–04: 2-col grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {[
              {
                num: "01",
                title: "Lead Calculator",
                desc: "Work backward from your student start goal to know exactly how many leads you need to generate, contact, schedule, and show to hit your number.",
                outcome: "Stop guessing. Start with the math.",
              },
              {
                num: "02",
                title: "Start Tracker",
                desc: "Log projected and actual student starts by program (day, night, cosmetology, barbering, nails, and more). Monthly payment plans calculate automatically per student.",
                outcome: "See every start date's revenue contribution in real time.",
              },
              {
                num: "03",
                title: "Tuition Income Tracker",
                desc: "Project monthly tuition revenue by program, account for student drops, and track the real financial impact of attrition before it blindsides you.",
                outcome: "Know your income months before it arrives.",
              },
              {
                num: "04",
                title: "Clinic Sales Calculator",
                desc: "Set monthly clinic floor revenue goals. The calculator breaks it down to daily and per-student targets — so your floor instructors know exactly what success looks like every day.",
                outcome: "Manage the floor with data, not gut feel.",
              },
            ].map((m) => (
              <div key={m.num} className="bg-white border border-gray-200 rounded-xl p-6">
                <p className="text-[#a4a0c6] text-xs font-bold uppercase tracking-widest mb-2">{m.num}</p>
                <h3 className="text-base font-bold text-[#2a496e] mb-2">{m.title}</h3>
                <p className="text-xs text-gray-600 leading-relaxed mb-4">{m.desc}</p>
                <span className="inline-block bg-[#e4f6f8] text-[#2e97a9] text-xs font-bold px-2.5 py-1 rounded-md">
                  → {m.outcome}
                </span>
              </div>
            ))}
          </div>

          {/* Module 05: full-width, light Plum bg */}
          <div className="bg-[#f5eefa] border border-gray-200 rounded-xl p-6">
            <p className="text-[#a4a0c6] text-xs font-bold uppercase tracking-widest mb-2">05</p>
            <h3 className="text-base font-bold text-[#2a496e] mb-2">Profit &amp; Loss Dashboard</h3>
            <p className="text-xs text-gray-600 leading-relaxed mb-4">
              Combine all revenue sources — tuition income, clinic sales, drop adjustments — with your monthly expenses to see a clear
              monthly P&amp;L view. Track positive and negative months in advance. Export reports for accreditation documentation.
            </p>
            <span className="inline-block bg-[#e4f6f8] text-[#2e97a9] text-xs font-bold px-2.5 py-1 rounded-md">
              → Run your school like a CFO. Without hiring one.
            </span>
          </div>
        </div>
      </section>

      {/* ── WHO IT'S FOR ─────────────────────────────────────────────────── */}
      <section className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[#2e97a9] text-xs font-bold uppercase tracking-widest mb-3">Who it's for</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2a496e]">
              Built for every leader who runs a beauty or barber school.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                role: "Independent School Owner",
                desc: "You built this school from the ground up. Now you need a financial tool built for the way your school actually works. Replaces spreadsheets, manual tracking, and guesswork.",
                tag: "Replaces spreadsheets & guesswork.",
              },
              {
                role: "Campus Director / Administrator",
                desc: "You're responsible for hitting the revenue number. This tool gives you the operational data to manage your team's performance against it.",
                tag: "Bridges goals and ground-level execution.",
              },
              {
                role: "School Owner in Accreditation",
                desc: "NACCAS requires documented evidence of financial viability. Revenue Pro Planner™ gives you the forward-cast data your auditor needs to see.",
                tag: "Supports financial stability documentation for your ISS.",
              },
            ].map((a) => (
              <div
                key={a.role}
                className="bg-white border border-gray-200 rounded-xl p-6"
                style={{ borderTop: "3px solid #2e97a9" }}
              >
                <h3 className="text-sm font-bold text-[#2a496e] mb-2">{a.role}</h3>
                <p className="text-xs text-gray-600 leading-relaxed mb-4">{a.desc}</p>
                <p className="text-xs italic text-[#3d184e]">{a.tag}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOUNDER ─────────────────────────────────────────────────────── */}
      <section id="about" className="bg-[#f3fafb] py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-10 items-start">
            {/* Left: identity block */}
            <div className="md:col-span-2 flex flex-col items-center md:items-start gap-4">
              <p className="text-[#2e97a9] text-xs font-bold uppercase tracking-widest">Why this tool exists</p>
              <div className="w-16 h-16 bg-[#3d184e] rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                CJ
              </div>
              <div>
                <p className="text-[#2a496e] font-bold text-base">Carla Jones James, MBA</p>
                <p className="text-[#2e97a9] text-xs mt-0.5 leading-relaxed">
                  Fractional Operations &amp; Accreditation Officer · Founder, The Beauty School Group™
                </p>
              </div>
            </div>

            {/* Right: content */}
            <div className="md:col-span-3">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#2a496e] mb-5">
                Built by someone who ran the numbers every day.
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Carla Jones James, MBA built the original version of Revenue Pro Planner™ as a working spreadsheet when she was serving
                as a campus president — responsible for hitting enrollment goals, managing clinic floor revenue, and reporting monthly
                P&amp;L to corporate leadership. After years using that system to manage multi-program campuses and serving as National
                Director of Clinic at Empire Beauty Schools, she transformed it into a platform any school owner can use.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mb-6">
                Certified peer evaluator for both NACCAS and ABHES with 8+ years of experience.
              </p>

              {/* Pull quote */}
              <blockquote
                className="bg-[#fdf6e3] px-5 py-4 rounded-r-xl"
                style={{ borderLeft: "3px solid #C9A84C" }}
              >
                <p className="text-sm italic text-[#3d184e] leading-relaxed">
                  "I built this for the school owner who is doing everything right — and still can't see the numbers clearly enough
                  to protect what they've built."
                </p>
                <p className="text-xs font-bold text-[#2a496e] mt-2">
                  — Carla Jones James, MBA | Founder, Revenue Pro Planner™
                </p>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ─────────────────────────────────────────────────────── */}
      <section id="pricing" className="bg-white py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#2e97a9] text-xs font-bold uppercase tracking-widest mb-3">Pricing</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2a496e]">
              Simple pricing. No contracts. No complexity.
            </h2>
          </div>

          <div className="max-w-md mx-auto">
            <div className="bg-[#fdf8ff] border-2 border-[#3d184e] rounded-2xl p-8 relative">
              {/* Free trial badge */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-[#2e97a9] text-white text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap">
                  Start with a free 7-day trial
                </span>
              </div>

              <p className="text-xs text-[#2e97a9] font-bold uppercase tracking-widest mb-2 mt-2 text-center">Monthly Plan</p>
              <div className="flex items-end gap-1 mb-1 justify-center">
                <p className="text-4xl font-bold text-[#2a496e]">${monthlyPrice}</p>
                <p className="text-gray-400 text-sm mb-1">/month</p>
              </div>
              <p className="text-xs text-gray-500 mb-6 text-center">After your 7-day free trial · Cancel anytime</p>

              <ul className="space-y-2.5 mb-8">
                {[
                  "7 days completely free — no credit card required",
                  "All 5 modules unlocked",
                  "Unlimited programs and start dates",
                  "Multi-year forward projections",
                  "P&L reports exportable for accreditation",
                  "Cancel anytime",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-xs text-gray-700">
                    <FiCheck className="text-[#2e97a9] flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => navigate("/trial")}
                className="w-full bg-[#C9A84C] text-[#3d184e] font-bold text-sm py-3.5 rounded-xl hover:brightness-110 transition-all"
              >
                Start My Free Trial
              </button>
              <p className="text-center text-xs text-gray-400 mt-3">
                No credit card required · Cancel anytime
              </p>
            </div>
          </div>

          <p className="text-center text-xs text-gray-400 mt-6">
            No long-term commitment. Built for school owners, not enterprise finance teams.
          </p>
        </div>
      </section>

      {/* ── FINAL CTA ───────────────────────────────────────────────────── */}
      <section className="bg-[#3d184e] py-20">
        <div className="max-w-xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Your revenue doesn't have to be a mystery anymore.
          </h2>
          <p className="text-[#a4a0c6] text-base mb-8 leading-relaxed">
            Start your free trial today and see exactly where your school's money is coming from — and where it's going.
          </p>
          <button
            onClick={() => navigate("/trial")}
            className="bg-[#C9A84C] text-[#3d184e] font-bold px-10 py-4 rounded-xl text-base hover:brightness-110 transition-all"
          >
            Start My Free Trial
          </button>
          <p className="text-[#a4a0c6] text-xs mt-4 opacity-60">
            No credit card required · Cancel anytime · Built specifically for beauty and barber schools.
          </p>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────────── */}
      <footer className="bg-[#2a1a35] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <p className="text-white font-bold text-base mb-1">
                Revenue Pro Planner<span className="text-[#C9A84C]">™</span>
              </p>
              <p className="text-[#a4a0c6] text-xs leading-relaxed mt-1">
                The Revenue Intelligence Platform for Beauty &amp; Barber Schools.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Navigation</h4>
              <ul className="space-y-2 text-[#a4a0c6] text-xs">
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Login</Link></li>
                <li><Link to="/trial" className="hover:text-white transition-colors">Start Free Trial</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Legal</h4>
              <ul className="space-y-2 text-[#a4a0c6] text-xs">
                <li><span className="text-[#a4a0c6] cursor-default">Privacy Policy</span></li>
                <li><span className="text-[#a4a0c6] cursor-default">Terms of Service</span></li>
                <li>
                  <a href="mailto:Support@revenueproplanner.com" className="hover:text-white transition-colors">
                    Support@revenueproplanner.com
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-6 text-center space-y-1" style={{ borderTop: "1px solid rgba(164,160,198,0.2)" }}>
            <p className="text-[#a4a0c6] text-xs">
              Revenue Pro Planner™ is a product of The Beauty School Group™
            </p>
            <p className="text-xs" style={{ color: "rgba(164,160,198,0.5)" }}>
              © 2025 The Beauty School Group, LLC. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
